import { type ReactNode, useId, useRef } from "react";
import { createPortal } from "react-dom";

import { cn } from "./lib/cn";
import { useDismiss, useFocusTrap } from "./lib/useOverlay";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  footer?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
}

/** Genişlikler §3: küçük 480 / orta 560 (Yeni Müvekkil, Temsilci Davet) / geniş 720. */
const sizeClasses: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-[480px]",
  md: "max-w-[560px]",
  lg: "max-w-[720px]",
};

export function Modal({ open, onClose, title, footer, children, size = "md" }: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * Odak kapanı ve ESC ortak kancalarda (CLOS-95 `[056]`): aynı davranışı drawer ve üst
   * bar panelleri de isteyecek. Davranış çıkarma sırasında DEĞİŞMEDİ.
   *
   * `outsideClick` KAPALI: örtüye tıklama zaten aşağıda dinleniyor, ikisi birden açık
   * olsaydı `onClose` iki kez çağrılırdı.
   */
  useFocusTrap(panelRef, open);
  useDismiss(panelRef, open, onClose);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        data-testid="modal-overlay"
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-inverse/40 backdrop-blur-[2px]"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "relative flex w-full flex-col rounded-lg bg-surface shadow-lg",
          sizeClasses[size],
        )}
      >
        {/* Başlık şeridinin altındaki çizgi kalktı: kart deseniyle aynı gerekçe (boşluk ayırır). */}
        <header className="flex items-start justify-between gap-3 px-8 pt-8 pb-2">
          <h2 id={titleId} className="text-xl font-bold text-fg-strong">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Kapat"
            onClick={onClose}
            className="-mt-1.5 -mr-1.5 flex size-10 shrink-0 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-muted hover:text-fg-muted"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto px-8 py-4">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-border-subtle px-8 py-6">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
